import { useState, useCallback } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Zap, 
  Activity, 
  Triangle, 
  History, 
  ChevronRight,
  ChevronLeft,
  Lightbulb,
  Info
} from "lucide-react";
import { DCPowerTab } from "@/components/calculator/DCPowerTab";
import { SinglePhaseTab } from "@/components/calculator/SinglePhaseTab";
import { ThreePhaseTab } from "@/components/calculator/ThreePhaseTab";
import { CalculationHistory } from "@/components/calculator/CalculationHistory";
import { PowerTypesTable } from "@/components/calculator/PowerTypesTable";
import { QuickTips } from "@/components/calculator/QuickTips";

// Helper to load history from localStorage
const loadHistoryFromStorage = () => {
  if (typeof window === 'undefined') return [];
  const savedHistory = localStorage.getItem("powerCalcHistory");
  if (savedHistory) {
    try {
      return JSON.parse(savedHistory);
    } catch (e) {
      console.error("Failed to parse history:", e);
      return [];
    }
  }
  return [];
};

export const PowerCalculator = () => {
  const [activeTab, setActiveTab] = useState("dc");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState(loadHistoryFromStorage);

  // Save calculation to history
  const saveToHistory = useCallback((calculation) => {
    setHistory((prev) => {
      const newHistory = [
        { ...calculation, id: Date.now(), timestamp: new Date().toISOString() },
        ...prev
      ].slice(0, 5); // Keep only last 5
      localStorage.setItem("powerCalcHistory", JSON.stringify(newHistory));
      return newHistory;
    });
  }, []);

  // Clear history
  const clearHistory = useCallback(() => {
    setHistory([]);
    localStorage.removeItem("powerCalcHistory");
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10 border border-primary/20">
                <Zap className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-lg sm:text-xl font-semibold text-foreground">
                  Power Calculator Pro
                </h1>
                <p className="text-xs text-muted-foreground hidden sm:block">
                  Electrical Design Tools
                </p>
              </div>
            </div>
            
            {/* History Toggle */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setHistoryOpen(!historyOpen)}
              className="gap-2 border-border hover:bg-secondary hover:border-primary/50"
            >
              <History className="h-4 w-4" />
              <span className="hidden sm:inline">History</span>
              {history.length > 0 && (
                <span className="bg-primary text-primary-foreground text-xs px-1.5 py-0.5 rounded-full">
                  {history.length}
                </span>
              )}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex gap-6">
          {/* Main Calculator Area */}
          <div className="flex-1 min-w-0">
            {/* Calculator Tabs */}
            <Card className="bg-card border-border shadow-card mb-6">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <CardHeader className="pb-0">
                  <TabsList className="w-full grid grid-cols-3 bg-secondary/50 p-1 h-auto">
                    <TabsTrigger 
                      value="dc" 
                      className="gap-2 py-3 data-[state=active]:bg-card data-[state=active]:text-primary data-[state=active]:shadow-sm"
                    >
                      <Zap className="h-4 w-4" />
                      <span className="hidden sm:inline">DC Power</span>
                      <span className="sm:hidden">DC</span>
                    </TabsTrigger>
                    <TabsTrigger 
                      value="single" 
                      className="gap-2 py-3 data-[state=active]:bg-card data-[state=active]:text-primary data-[state=active]:shadow-sm"
                    >
                      <Activity className="h-4 w-4" />
                      <span className="hidden sm:inline">Single-Phase AC</span>
                      <span className="sm:hidden">1Φ AC</span>
                    </TabsTrigger>
                    <TabsTrigger 
                      value="three" 
                      className="gap-2 py-3 data-[state=active]:bg-card data-[state=active]:text-primary data-[state=active]:shadow-sm"
                    >
                      <Triangle className="h-4 w-4" />
                      <span className="hidden sm:inline">Three-Phase AC</span>
                      <span className="sm:hidden">3Φ AC</span>
                    </TabsTrigger>
                  </TabsList>
                </CardHeader>

                <CardContent className="pt-6">
                  <TabsContent value="dc" className="mt-0 animate-fade-in">
                    <DCPowerTab onSaveHistory={saveToHistory} />
                  </TabsContent>
                  
                  <TabsContent value="single" className="mt-0 animate-fade-in">
                    <SinglePhaseTab onSaveHistory={saveToHistory} />
                  </TabsContent>
                  
                  <TabsContent value="three" className="mt-0 animate-fade-in">
                    <ThreePhaseTab onSaveHistory={saveToHistory} />
                  </TabsContent>
                </CardContent>
              </Tabs>
            </Card>

            {/* Educational Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Power Types Table */}
              <Card className="bg-card border-border shadow-card">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base font-medium">
                    <Info className="h-4 w-4 text-primary" />
                    Power Types Explained
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <PowerTypesTable />
                </CardContent>
              </Card>

              {/* Quick Tips */}
              <Card className="bg-card border-border shadow-card">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base font-medium">
                    <Lightbulb className="h-4 w-4 text-accent" />
                    Quick Tips
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <QuickTips />
                </CardContent>
              </Card>
            </div>
          </div>

          {/* History Sidebar */}
          <div 
            className={`fixed right-0 top-16 h-[calc(100vh-4rem)] bg-card border-l border-border shadow-xl z-40 transition-transform duration-300 ${
              historyOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
            style={{ width: '320px' }}
          >
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h2 className="font-medium text-foreground flex items-center gap-2">
                <History className="h-4 w-4" />
                Calculation History
              </h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setHistoryOpen(false)}
                className="h-8 w-8"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <CalculationHistory 
              history={history} 
              onClear={clearHistory}
            />
          </div>

          {/* History toggle button when closed */}
          {!historyOpen && history.length > 0 && (
            <Button
              variant="outline"
              size="icon"
              onClick={() => setHistoryOpen(true)}
              className="fixed right-4 top-24 z-30 bg-card border-border hover:bg-secondary hover:border-primary/50 shadow-lg"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-border bg-card/30 mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
            <p>
              For reference only — always verify calculations with NEC/CEC requirements.
            </p>
            <p>© 2026 Power Calculator Pro. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};
